import { Button, Typography } from '@modulor/react';
import SuccessStatusIcon from 'assets/SuccessStatusIcon';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC } from 'react';
import { useAppSelector } from 'store/hooks';
import type { PayHubCashierContractsReversePendingWithdrawalV2Response } from 'store/models';
import { currencyDisplaySelector } from 'store/slices/deposit';
import { getFormattedAmountWithCurrency } from 'utils';

interface PartialReverseResultProps {
  result: PayHubCashierContractsReversePendingWithdrawalV2Response;
  onDeposit: () => void;
}

// TODO: align with the success screen design in Figma
export const PartialReverseResult: FC<PartialReverseResultProps> = ({ result, onDeposit }) => {
  const { translate } = useSkeletonTranslation();
  const currencyDisplaySetting = useAppSelector(currencyDisplaySelector);
  const formatAmount = (amount: number) =>
    getFormattedAmountWithCurrency(`${amount}`, result.currency, currencyDisplaySetting);

  // amounts come from the response, not from the FE preview
  const rows = [
    {
      label: translate('PH.DEPOSIT.PARTIAL_REVERSE.RESULT.RETURNED', { defaultValue: 'Returned to your account' }),
      value: formatAmount(result.success_amount),
    },
    {
      label: translate('PH.DEPOSIT.PARTIAL_REVERSE.RESULT.REMAIN', { defaultValue: 'Remains in withdrawal' }),
      value: formatAmount(result.remain_amount),
    },
  ];

  return (
    <div className="partial-reverse-result" data-testid="partial-reverse-result">
      <SuccessStatusIcon />
      <Typography variant="title-2-semibold" tag="h1">
        {translate('PH.DEPOSIT.PARTIAL_REVERSE.RESULT.TITLE', { defaultValue: 'Money returned' })}
      </Typography>
      <div className="partial-reverse-result__rows">
        {rows.map(({ label, value }) => (
          <div key={label} className="partial-reverse-result__row">
            <Typography variant="subhead-regular" tag="span">
              {label}
            </Typography>
            <Typography variant="subhead-semibold" tag="span">
              {value}
            </Typography>
          </div>
        ))}
      </div>
      <Button className="partial-reverse-result__button" variant="deposit" onClick={onDeposit}>
        {translate('CASHIER.DEPOSIT')}
      </Button>
    </div>
  );
};
